import { Menu, Transition } from "@headlessui/react";
import { ArrowLeftIcon, ChevronDownIcon } from "@heroicons/react/outline";
import PaystackPop from "@paystack/inline-js";
import Button from "components/lib/Shared/Button";
import { Constants } from "config/constants";
import { Formik } from "formik";
import { usePopper } from "hooks/usePopper";
import { useToast } from "hooks/useToast";
import _pick from "lodash/pick";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getCurrentUser } from "state/slices/auth";
import { closeModal, openModal } from "state/slices/modals";
import { generateOrderIdAsync } from "state/slices/order";
import {
  chargeSavedCardAsync,
  initializeTransactionAsync,
  verifyTransactionAsync,
} from "state/slices/paystack";
import { getFullName } from "utils";

const PaystackPopup = ({
  discountPercentage = 0,
  selectedCurrency,
  abbrevCompanyType = "PLC",
  btnDetails = {
    text: "Subscribe",
    icon: null,
    className: "bg-sflPrimary text-white w-full p-1",
  },
  handleSubscribe = () => {},
}) => {
  const paystackRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState(null);
  const [invoice, setInvoice] = useState({});
  const dispatch = useDispatch();
  const history = useHistory();
  const { toast } = useToast();
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  const userSlice = useSelector((state) => state.userSlice);
  const sflData = Constants.SFL_DATA;
  const savedCards = userSlice?.savedCards || [];
  let latestCard = null;
  if (savedCards.length > 0) {
    latestCard = savedCards[savedCards.length - 1];
  }
  let orgAdminProfitMargin = null;
  if (userSlice?.orgAdmin) {
    orgAdminProfitMargin = selectedCurrency?.orgAdminProfitMargins?.find(
      (profitmargin) =>
        profitmargin?.orgAdmin?.toString() === userSlice.orgAdmin?.toString()
    )?.profitMargin;
  }

  const developActualAmount = 3;
  const productionActualAmount =
    (selectedCurrency.basePrice +
      (orgAdminProfitMargin
        ? orgAdminProfitMargin
        : selectedCurrency?.profitMargin ?? 0)) *
    selectedCurrency.applicableTax;

  const actualAmount =
    process.env.REACT_APP_ENV === "develop"
      ? developActualAmount
      : productionActualAmount;

  const discountAmount = discountPercentage
    ? actualAmount * (discountPercentage / 100)
    : 0;
  const totalAmount = actualAmount - discountAmount;
  const paystackAmount = totalAmount * 100;

  const initialValues = {
    firstName: userSlice?.names?.firstName ?? "",
    lastName: userSlice?.names?.lastName ?? "",
    email: userSlice?.email ?? "",
    mobileNumber: userSlice?.electronicAddress?.mobileNumber ?? "",
    billingAddress: [
      "Kenyan Citizen",
      "Foreign Resident",
      "Local Company",
    ].includes(userSlice.idType)
      ? `PO BOX ${userSlice?.postalAddress?.postalAddress} ${userSlice?.postalAddress?.postalCode}`
      : userSlice?.postalAddress?.postalAddress,
    paymentMethod: "mpesa",
    mpesaPhoneNumber:
      userSlice?.mpesaPhoneNumber ??
      userSlice?.electronicAddress?.mobileNumber?.replace("+", "") ??
      "",
    cardHolderName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
  };

  const generateInvoice = (values) => {
    const { paymentMethod } = values;
    const invoice = {
      plan: "subscription",
      billFrom: {
        name: sflData.name,
        address: `${sflData.buildingName}, ${sflData.floorNumber}, ${sflData.streetName}, ${sflData.location.county}`,
        pin: sflData.pin,
      },
      billTo: {
        name: getFullName(userSlice.names),
        address: values.billingAddress,
        pin: userSlice.identificationDetails?.KRAPin,
      },
      items: [
        {
          description: "subscription payment",
          quantity: 1,
          actualAmount,
          discountAmount,
          rate: totalAmount,
        },
      ],
      currency: selectedCurrency.type,
      isTaxApplicable: selectedCurrency.applicableTax > 1,
      applicableTax: selectedCurrency.applicableTax,
      paymentMethod,
      ..._pick(values, ["firstName", "email"]),
    };

    return invoice;
  };

  const verifyTransaction = async (reference, invoice) => {
    const generateOrderIdResponse = await dispatch(
      generateOrderIdAsync({ type: abbrevCompanyType })
    );

    const orderId = generateOrderIdResponse.payload.orderId;
    const payload = {
      company: { type: abbrevCompanyType },
      orderId,
      invoice,
      user: userSlice,
    };

    // Verify on your backend before you “confirm” payment
    const res = await dispatch(
      verifyTransactionAsync({ reference, data: payload })
    );

    if (res.payload.data) {
      handleSubscribe();
    }
  };

  const initializePaystackPopup = () => {
    // Initiate paystack popup
    const popup = new PaystackPop();
    popup.resumeTransaction(accessCode, {
      onSuccess: async (tx) => {
        // tx.reference is what you verify server-side
        const ref = tx.reference;
        await verifyTransaction(ref, invoice);
      },

      onCancel: () => {
        setLoading(false);
      },

      onError: (err) => {
        setLoading(false);
        history.push("/payment-failed");
      },
    });
  };

  useEffect(() => {
    if (accessCode) {
      initializePaystackPopup();
    }
  }, [accessCode]);

  const handlePayWithPaystackPopup = async (values) => {
    setLoading(true);
    setInvoice(generateInvoice(values));
    const response = await dispatch(
      initializeTransactionAsync({
        data: {
          email: values?.email,
          amount: paystackAmount, // paystack expects the least denomination i.e amount in cents,
        },
      })
    );
    if (response.payload?.data?.access_code) {
      setAccessCode(response.payload.data?.access_code);
    } else {
      toast(
        "error",
        response.payload?.messageText ?? "Could not initialize transaction"
      );
      setLoading(false);
    }
    return;
  };

  const handlePayWithSavedCard = async (values) => {
    setLoading(true);
    const invoice = generateInvoice(values);
    const chargeResponse = await dispatch(
      chargeSavedCardAsync({
        data: {
          email: latestCard.customer.email,
          authorization_code: latestCard.authorization.authorization_code,
          amount: paystackAmount > 300 ? paystackAmount : 300,
        },
      })
    );
    if (chargeResponse.payload?.data?.status === "success") {
      await verifyTransaction(chargeResponse.payload.data.reference, invoice);
      setLoading(false);
      dispatch(closeModal("payment_confirmation_modal"));
    } else {
      toast(
        "error",
        chargeResponse.payload?.data?.gateway_response ||
          "Failed to charge the saved card"
      );
      handlePayWithPaystackPopup(values);
    }
  };

  const handleMakePayment = async (values) => {
    try {
      if (savedCards?.length > 0) {
        handlePayWithSavedCard(values);
      } else {
        handlePayWithPaystackPopup(values);
      }
    } catch (err) {
      toast("error", "Something went wrong, please try again.");
    }
  };

  return (
    <div>
      <Formik enableReinitialize initialValues={initialValues}>
        {({ values, errors }) => {
          return (
            <div className="text-sm w-full">
              <Button
                className={btnDetails.className}
                isLoading={loading}
                type="submit"
                loadingText="Processing..."
                onClick={() => handleMakePayment(values)}
              >
                {btnDetails.icon && (
                  <span className="mr-2">{btnDetails.icon}</span>
                )}
                {btnDetails.text}
              </Button>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default PaystackPopup;
